import classnames from 'classnames'
import React, { memo } from 'react'
import { Pagination, Select } from 'antd'
import type { PaginationProps } from 'antd'
import { useMemoizedFn } from 'ahooks'

import { Merge } from '@/utils/utilityTypes'

import { Button } from '@/ui'

import styles from './index.less'

const DEFAULT_CURRENT = 1
const DEFAULT_PAGE_SIZE = 20
const FIRST_PAGE = 1
const PAGE_STEP = 1
const INFINITE_TOTAL_THRESHOLD = 0

type BaseProps = Pick<
    PaginationProps,
    'current' | 'pageSize' | 'pageSizeOptions' | 'total' | 'onChange' | 'disabled' | 'className' | 'style'
>

interface ExtendProps {
    /** 是否有下一页 */
    hasNext?: boolean
}

export type InfinitePaginationProps = Merge<BaseProps, ExtendProps>

/** 分页器，Antd Pagination Compatible，额外支持了不确定总条数情况下的翻页功能 */
export const InfinitePagination = memo((props: InfinitePaginationProps) => {
    const {
        current = DEFAULT_CURRENT,
        pageSize = DEFAULT_PAGE_SIZE,
        pageSizeOptions,
        total,
        onChange,
        disabled,
        hasNext,
        className,
        style,
    } = props
    const isInfiniteTotal = (total ?? INFINITE_TOTAL_THRESHOLD) < INFINITE_TOTAL_THRESHOLD

    const handlePrev = useMemoizedFn(() => {
        onChange?.(current - PAGE_STEP, pageSize)
    })

    const handleNext = useMemoizedFn(() => {
        onChange?.(current + PAGE_STEP, pageSize)
    })

    const handlePageSizeChange = useMemoizedFn((value: number) => {
        onChange?.(FIRST_PAGE, value)
    })

    if (!isInfiniteTotal) {
        return <Pagination {...props} />
    }

    return (
        <div className={classnames(styles.infinitePagination, className)} style={style}>
            <Button type="link-compact" disabled={disabled || current <= FIRST_PAGE} onClick={handlePrev}>
                上一页
            </Button>
            <Button type="link-compact" disabled={disabled || !hasNext} onClick={handleNext}>
                下一页
            </Button>
            {pageSizeOptions?.length ? (
                <Select
                    className={styles.pageSizeSelect}
                    size="small"
                    options={pageSizeOptions.map(v => ({
                        label: `${v} 条/页`,
                        value: Number(v),
                    }))}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    disabled={disabled}
                    allowClear={false}
                />
            ) : null}
        </div>
    )
})
